/**
 * Your website — the hub, and [dukaan §3](../../../../../docs/legacy/dukaan.md)'s
 * steps 4 and 5: **preview** and **publish**.
 *
 * ## Wave 14: one panel on top, the builder as cards below
 *
 * C13 (spec §10.5 item 1; ADR-0032 §1): *Your website* opens on **one panel**
 * — draft and live versions, **Publish**, and the draft link with Copy and
 * Open — and under it a card grid in the Settings hub's manner
 * (`components/HubCard`, shared with it), grouped **Look**, **Content**,
 * **Clients & visitors** and **Rules**. Each card says what is set now in one
 * line, read from the page's own API; each read may fail on its own, and a
 * card whose read failed shows its description and no line, never a guess.
 * *Client sign-in*, *Domain* and *Social profiles & map* came here from
 * Settings in the same wave, because everything a visitor or a client sees is
 * edited under the website.
 *
 * ## Draft and published are two versions, and the screen never blurs them
 *
 * FR-I9: the site has a draft and a published version, and publish is an
 * explicit act. Everything under `/site` edits the draft — the gallery, the
 * theme panel, the pages, the blog. The live site does not change until a
 * partner presses the button here. A firm that cannot tell which it is looking
 * at will eventually publish something it was still writing.
 *
 * ## The preview link
 *
 * FR-I11, rewritten by **C6** ([ADR-0024](../../../../../docs/adr/0024-the-preview-link-belongs-to-the-site-and-it-is-always-there.md)):
 * the firm has **one** link, it is here from the first time this screen loads,
 * and it always shows whatever the draft says now. There is nothing to press to
 * get one and nothing to keep — the address is on the card on every visit.
 *
 * What that replaced is worth keeping written down, because every line of it
 * was a sentence this screen used to say to a firm: the token came back once
 * (*"copy it now — this is the only time it is shown"*), it died at every
 * publish, its window was seven flat days from minting, and with no bound
 * domain there was no link at all (*"a preview link needs an address to point
 * at, so it waits for your domain"*). The last of those contradicted FR-I11's
 * own promise that a preview works **before** the domain is bound.
 *
 * The link now lives at `/_preview/<token>` on this very origin, so **Copy**
 * and **Open** both work from the moment a firm has anything to show. The two
 * buttons beside it are the only decisions left: *Replace* (the address went to
 * the wrong person) and *Turn it off* — and turning it off **sticks**, which is
 * the one behaviour a reader would not predict from "it makes itself".
 *
 * ## Publish is behind step-up
 *
 * T-4.6, and the gate Wave 3C marked as a seam and left off. Publishing puts a
 * professional practice's words on its public address under a regulator's
 * advertising guidelines, and it cannot be unsent — the page is fetched, cached
 * and indexed before anyone notices, and re-publishing is not an undo because
 * it promotes the *current* draft.
 *
 * **The challenge is `components/StepUpDialog`, wired at the Wave 4 close.**
 * This screen shipped with a link to the accounts app instead, because when it
 * was written `app/platform/stepup.py`'s own docstring said the assertion came
 * from `core-mfa` — which mints none and is not callable from a browser — so
 * the honest conclusion was that no environment could pass the gate, and that
 * is also why the local compose turned `PRODESK_STEPUP_REQUIRED` off. Wave 4C
 * read the running stack the same day and found the two routes on `core-auth`.
 * The gate is on locally from the close, and it is passed here the way it is
 * passed on an invoice: core's own challenge, an assertion used once.
 *
 * `STEPUP_REQUIRED` opens the dialog. `STEPUP_INVALID` does **not** — the
 * re-verification was attempted and refused, and re-launching the challenge on
 * that answer is a loop. Two codes, two behaviours.
 */

import { Alert, Badge, Button, ConfirmDialog, buttonClass } from '@fiveox/ui'
import type { LucideIcon } from 'lucide-react'
import {
  Brush,
  CalendarCheck,
  Check,
  Copy,
  ExternalLink,
  FileText,
  Globe,
  Images,
  Info,
  KeyRound,
  Link2,
  Menu,
  Newspaper,
  Palette,
  Scale,
  Send,
  Share2,
  Wand2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

import { overrideCount } from '../../components/ConductSwitches'
import { HubCard, HubGroup } from '../../components/HubCard'
import { StepUpDialog } from '../../components/StepUpDialog'
import { BOOKING, SITE, type Verb, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { booking } from '../../lib/api/booking'
import { type DomainBinding, type SummaryLine, firm } from '../../lib/api/firm'
import { type SignInChoice, signIn, signInSummary } from '../../lib/api/signIn'
import { previewDaysLeft, previewUrl, site } from '../../lib/api/site'
import { siteMedia } from '../../lib/api/siteMedia'
import { FONTS } from '../../../site/fonts'
import { useSite } from './useSite'

export function SiteHome() {
  const { can } = useEvaluation()
  const publishable = can(SITE, 'can_update')
  const {
    site: current,
    vocabulary,
    draft,
    published,
    isLive,
    preview,
    loading,
    error,
    refresh,
  } = useSite()

  const [copied, setCopied] = useState(false)
  const [busy, setBusy] = useState<'preview' | 'revoke' | 'publish' | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [stepUpNeeded, setStepUpNeeded] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const facts = useHubFacts()

  const domain = facts.data.domain ?? null
  const domainState = domain?.state ?? null
  const host = domain?.state === 'bound' ? domain.host : null

  // Both of these are *decisions*, not the way a firm gets a link — the link
  // is already there by the time this screen renders (C6, ADR-0024 §4).
  const replaceLink = async () => {
    setBusy('preview')
    setFailure(null)
    setCopied(false)
    try {
      await site.replacePreview()
      await refresh()
      setNotice('New link made. The old one has stopped working.')
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : 'A new preview link could not be made.')
    } finally {
      setBusy(null)
    }
  }

  const revokeLink = async () => {
    setBusy('revoke')
    setFailure(null)
    setCopied(false)
    try {
      await site.revokePreview()
      await refresh()
      setNotice('Your preview link is off. Nobody holding it can open your draft.')
    } catch (err) {
      setFailure(err instanceof ApiError ? err.message : 'That link could not be turned off.')
    } finally {
      setBusy(null)
    }
  }

  const publish = async (assertion?: string) => {
    setBusy('publish')
    setFailure(null)
    setNotice(null)
    setStepUpNeeded(false)
    try {
      await site.publish(undefined, assertion)
      setVerifying(false)
      await refresh()
      setNotice('Published. Your site is live.')
    } catch (err) {
      if (err instanceof ApiError && err.code === 'STEPUP_REQUIRED') {
        // Not an error message — the product asking the person to prove they
        // are still the person. The dialog says which irreversible thing.
        setVerifying(true)
        setStepUpNeeded(true)
      } else if (err instanceof ApiError && err.code === 'STEPUP_INVALID') {
        setVerifying(false)
        setFailure('That re-verification did not go through. Nothing has been published.')
      } else {
        setVerifying(false)
        setFailure(err instanceof ApiError ? err.message : 'Your site could not be published.')
      }
    } finally {
      setBusy(null)
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading your website…</p>
  if (error) return <Alert tone="error">{error}</Alert>
  if (!current) return null

  const daysLeft = previewDaysLeft(preview)
  const link = previewUrl(preview?.path ?? null)
  const data: WebsiteHubData = {
    ...facts.data,
    template:
      vocabulary?.templates.find((t) => t.code === current.template)?.name ??
      current.template.replace(/^practice-/, ''),
    look: {
      font: current.resolved_theme?.font ?? current.theme?.font ?? null,
      logo: Boolean(current.logo_url),
    },
    social: {
      profiles: Object.values(current.social ?? {}).filter(Boolean).length,
      map: Boolean(current.contact?.map_url),
    },
  }
  // Only said when there is something to compare: a live site whose preview
  // shows the draft has changes waiting.
  const waiting = isLive && preview?.showing === 'draft'

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <header>
        <h1 className="text-2xl font-semibold text-foreground">Your website</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Everything a visitor or a client of your firm sees — the look, the pages, how clients sign
          in and what your institute lets you say. Each card opens its own page.
        </p>
      </header>

      {failure && <Alert tone="error">{failure}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {/* The gate, when it bites. Written as an instruction rather than as an
          error, because it is not one: it is the product asking the person to
          prove they are still the person. */}
      {stepUpNeeded && !verifying && (
        <Alert tone="warning">
          <strong className="font-medium">Publishing needs you to confirm it is you.</strong>{' '}
          Putting your site live is not something that can be undone quietly — the pages are
          fetched and cached the moment they are public. Press publish again and confirm the
          code we send you.
        </Alert>
      )}

      <StepUpDialog
        open={verifying}
        title="Publish your site"
        what={
          'Publishing puts your pages on your public address, where they are fetched, cached and ' +
          'indexed straight away. Re-publishing later promotes whatever your draft says then — it ' +
          'is not an undo.'
        }
        onClose={() => setVerifying(false)}
        onVerified={(assertion) => publish(assertion)}
      />

      {/* -- the one panel: draft and live, publish, the draft link --------- */}
      <section
        data-publish-panel=""
        aria-labelledby="site-status"
        className="overflow-hidden rounded-2xl border border-border bg-white shadow-sm"
      >
        <div className="flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <span
              className={`hidden h-12 w-12 shrink-0 items-center justify-center rounded-xl sm:flex ${
                isLive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}
            >
              <Globe size={22} aria-hidden />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 id="site-status" className="text-lg font-semibold text-foreground">
                  {isLive ? 'Your site is live' : 'Your site is not published yet'}
                </h2>
                <Badge tone={waiting ? 'warning' : isLive ? 'success' : 'neutral'} size="sm">
                  {waiting ? 'Changes waiting' : isLive ? 'Up to date' : 'Draft only'}
                </Badge>
              </div>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                {isLive
                  ? 'Everything you change here goes to a draft first. Your live site changes when you publish.'
                  : 'Nothing you do here is public until you publish it.'}
              </p>
              <dl className="mt-3 flex flex-col gap-1.5 text-sm sm:flex-row sm:flex-wrap sm:gap-x-6">
                <div className="flex gap-1.5">
                  <dt className="text-muted-foreground">Draft</dt>
                  <dd className="font-medium text-foreground">
                    {draft ? `Version ${draft.version}` : 'Nothing yet'}
                  </dd>
                </div>
                <div className="flex gap-1.5">
                  <dt className="text-muted-foreground">Live</dt>
                  <dd className="font-medium text-foreground">
                    {published
                      ? `Version ${published.version}${published.published_at ? ` · ${published.published_at.slice(0, 10)}` : ''}`
                      : 'Not published'}
                  </dd>
                </div>
                <div className="flex min-w-0 gap-1.5">
                  <dt className="text-muted-foreground">Address</dt>
                  <dd className="min-w-0 truncate font-medium text-foreground">
                    {host ? (
                      <a
                        href={`https://${host}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        {host} <ExternalLink size={12} aria-hidden />
                      </a>
                    ) : (
                      <Link to="/site/domain" className="text-primary hover:underline">
                        No domain yet
                      </Link>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-2">
            {/* C24 item 10 (ADR-0048 §3) — a firm that fills in its address and
                its services a week after signing up gets the benefit, which is
                the whole point of the action existing beside onboarding's. It
                is confirmed, because it replaces the draft's blocks. */}
            {publishable ? <RebuildButton onBuilt={() => void refresh()} /> : null}
            {publishable ? (
              <Button
                className="w-full lg:w-auto"
                disabled={!draft || busy !== null}
                loading={busy === 'publish'}
                loadingText="Publishing…"
                onClick={() => void publish()}
              >
                <Send size={14} aria-hidden /> {isLive ? 'Publish your changes' : 'Publish your site'}
              </Button>
            ) : (
              <p className="max-w-56 text-xs text-muted-foreground">
                Publishing is a partner&rsquo;s or a practice manager&rsquo;s to do.
              </p>
            )}
          </div>
        </div>

        {domainState !== null && domainState !== 'bound' && (
          <div className="flex items-start gap-2 border-t border-border bg-muted px-5 py-3 text-sm text-muted-foreground sm:px-6">
            <Info size={16} className="mt-0.5 shrink-0 text-primary" aria-hidden />
            <p>
              Your site needs your own domain before it can be seen by anyone. Everything else —
              writing the pages, choosing a template, previewing — works without one.{' '}
              <Link to="/site/domain" className="font-medium text-primary hover:underline">
                Set up your domain
              </Link>
            </p>
          </div>
        )}

        {/* -- the preview link ------------------------------------------- */}
        <div className="border-t border-border px-5 py-4 sm:px-6" data-draft-link="">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="shrink-0 lg:w-60">
              <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Link2 size={16} className="text-primary" aria-hidden /> Show someone your draft
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                One link, always showing what your draft says now. Search engines are told to
                ignore it.
              </p>
            </div>

            <div className="min-w-0 flex-1">
              {link ? (
                <div className="flex flex-wrap items-center gap-2">
                  <code className="min-w-0 flex-1 basis-full truncate rounded-lg bg-muted sm:basis-48 px-2.5 py-2 text-xs">
                    {link}
                  </code>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      void navigator.clipboard?.writeText(link)
                      setCopied(true)
                    }}
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
                  </Button>
                  {/* `buttonClass` rather than a `Button` with an `onClick`
                      that assigns `location` — the helper exists for exactly
                      this, and an anchor is what a person middle-clicks. */}
                  <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className={buttonClass({ variant: 'secondary', size: 'sm' })}
                  >
                    <ExternalLink size={14} /> Open
                  </a>
                </div>
              ) : preview?.state === 'revoked' ? (
                <p className="text-sm text-muted-foreground">
                  Your preview link is turned off. Anyone still holding the old address gets
                  nothing.
                </p>
              ) : preview?.state === 'lapsed' ? (
                <p className="text-sm text-muted-foreground">
                  Nobody has opened this link and nothing has changed behind it for a long time, so
                  it has stopped working. Make a new one.
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Write something on one of your pages and a link will appear here.
                </p>
              )}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted-foreground">
            {link && (
              <span className="min-w-0">
                {preview?.showing === 'published'
                  ? 'You have published everything you have written, so this shows your live site until you change something.'
                  : 'This is your draft. Nobody can see it unless you send them this link.'}
              </span>
            )}
            {daysLeft !== null && daysLeft < 15 && (
              <span>
                If nobody opens it and nothing changes, it stops working in {daysLeft}{' '}
                {daysLeft === 1 ? 'day' : 'days'}.
              </span>
            )}
            {/* Two decisions, and neither is how a firm *gets* a link — it has
                one already. Both are `site:can_update`, enforced here and at the
                API, so a viewer sees the address and no buttons. */}
            {publishable && preview?.state !== 'none' && (
              <span className="flex flex-wrap gap-1 sm:ml-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy !== null}
                  loading={busy === 'preview'}
                  loadingText="Making a link…"
                  onClick={() => void replaceLink()}
                >
                  {link ? 'Replace this link' : 'Make a new link'}
                </Button>
                {link && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy !== null}
                    loading={busy === 'revoke'}
                    loadingText="Turning it off…"
                    onClick={() => void revokeLink()}
                  >
                    Turn it off
                  </Button>
                )}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* -- the builder, as cards ----------------------------------------- */}
      {websiteGroups((resource, verb) => can(resource, verb)).map(({ group, cards }) => (
        <HubGroup
          key={group.key}
          id={`site-${group.key}`}
          title={group.title}
          description={group.description}
          columns={cards.length % 3 === 0 || cards.length === 1 ? 3 : 2}
        >
          {cards.map((card) => (
            <HubCard
              key={card.key}
              cardKey={card.key}
              href={card.to}
              icon={card.icon}
              title={card.title}
              description={card.note}
              loading={facts.pending.has(card.key)}
              line={card.summary(data)}
            />
          ))}
        </HubGroup>
      ))}
    </div>
  )
}

// -- the cards ---------------------------------------------------------------

export type WebsiteGroup = 'look' | 'content' | 'visitors' | 'rules'

export const WEBSITE_GROUPS: Array<{ key: WebsiteGroup; title: string; description: string }> = [
  {
    key: 'look',
    title: 'Look',
    description: 'The design your site uses, and its colours, type and logo.',
  },
  {
    key: 'content',
    title: 'Content',
    description: 'What your site says, and how a visitor finds their way round it.',
  },
  {
    key: 'visitors',
    title: 'Clients & visitors',
    description: 'How clients sign in, where your site lives, and where else to find you.',
  },
  {
    key: 'rules',
    title: 'Rules',
    description: 'What your institute’s guidelines let your site say.',
  },
]

/**
 * What a card's line reads from. A key that is `undefined` is not known —
 * its read has not come back, or failed — and its card shows no line.
 */
export interface WebsiteHubData {
  template?: string
  look?: { font: string | null; logo: boolean }
  pages?: number
  /** `true` once the firm has its own header menu; `false` while automatic. */
  menuCustom?: boolean
  posts?: { total: number; published: number }
  pictures?: number
  signIn?: SignInChoice
  domain?: DomainBinding
  social?: { profiles: number; map: boolean }
  conduct?: { overrides: number; noRules: boolean }
  /** w19b — the booking flows, and how many are live (C19). */
  bookingFlows?: { total: number; live: number }
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/**
 * **Everything the builder's home leads to, as a list** — and the list is the
 * finding (w8h, finding 2).
 *
 * Site → Theme is where the logo and the browser icon are set and always was.
 * This screen — the builder's home, the one a firm lands on to build its
 * website — had no tile for it. The only link to `/site/theme` anywhere on it
 * was a sentence inside the preview card, shown *only* to a firm with no domain
 * bound. So a UAT firm reported *"no option to add website logo and favicon"*
 * about a feature that had shipped, and it was right about the product even
 * though it was wrong about the code.
 *
 * A list rather than cards written out in the JSX so that *"the builder's home
 * reaches every builder screen in one click"* is a thing a test can read.
 * Wave 14 (C13) grouped it and gave each card its line of what is set now.
 */
/** A stored font key as the firm saw it on *How your site looks* ("Manrope", not "manrope"). */
function fontLabel(key: string | null): string | null {
  return key ? (FONTS.find((f) => f.key === key)?.label ?? key) : null
}

export const BUILDER_TILES: Array<{
  key: string
  to: string
  icon: LucideIcon
  title: string
  note: string
  group: WebsiteGroup
  summary: (data: WebsiteHubData) => SummaryLine | null
  /** A verb beyond `site:can_view` the page behind it needs. The card is
   *  dropped for a reader without it — the page would only refuse them. */
  needs?: [string, Verb]
}> = [
  {
    key: 'templates',
    to: '/site/templates',
    icon: Brush,
    title: 'Template',
    note: 'Which design your site uses.',
    group: 'look',
    summary: ({ template }) => (template ? { line: template, tone: 'set' } : null),
  },
  {
    key: 'theme',
    to: '/site/theme',
    icon: Palette,
    title: 'How your site looks',
    note: 'Colours, fonts, logo and browser icon.',
    group: 'look',
    summary: ({ look }) =>
      look
        ? {
            line: [fontLabel(look.font), look.logo ? 'your logo is on' : 'no logo yet']
              .filter(Boolean)
              .join(' · '),
            tone: look.logo ? 'set' : 'unset',
          }
        : null,
  },
  {
    key: 'pages',
    to: '/site/pages',
    icon: FileText,
    title: 'Pages',
    note: 'What each page of your site says.',
    group: 'content',
    summary: ({ pages }) =>
      pages === undefined
        ? null
        : pages === 0
          ? { line: 'No pages yet', tone: 'unset' }
          : { line: plural(pages, 'page'), tone: 'set' },
  },
  // w12c — FR-I15 item 4: beside Pages.
  {
    key: 'navigation',
    to: '/site/navigation',
    icon: Menu,
    title: 'Navigation',
    note: 'The line under your name, the menu and the footer.',
    group: 'content',
    summary: ({ menuCustom }) =>
      menuCustom === undefined
        ? null
        : menuCustom
          ? { line: 'Your own menu', tone: 'set' }
          : { line: 'Automatic menu — follows your pages', tone: 'set' },
  },
  {
    key: 'blog',
    to: '/site/blog',
    icon: Newspaper,
    title: 'Blog',
    note: 'Notes and updates for your clients.',
    group: 'content',
    summary: ({ posts }) =>
      !posts
        ? null
        : posts.total === 0
          ? { line: 'No posts yet', tone: 'unset' }
          : { line: `${plural(posts.total, 'post')} · ${posts.published} published`, tone: 'set' },
  },
  {
    key: 'media',
    to: '/site/media',
    icon: Images,
    title: 'Pictures',
    note: 'The images you have uploaded for your site.',
    group: 'content',
    summary: ({ pictures }) =>
      pictures === undefined
        ? null
        : pictures === 0
          ? { line: 'No pictures yet', tone: 'unset' }
          : { line: plural(pictures, 'picture'), tone: 'set' },
  },
  // w14a — moved here from Settings (C13, ADR-0032 §1).
  {
    key: 'client-sign-in',
    to: '/site/client-sign-in',
    icon: KeyRound,
    title: 'Client sign-in',
    note: 'How your clients sign in and sign up to your portal.',
    group: 'visitors',
    summary: ({ signIn: choice }) => (choice ? { line: signInSummary(choice), tone: 'set' } : null),
  },
  {
    key: 'domain',
    to: '/site/domain',
    icon: Globe,
    title: 'Domain',
    note: 'The address your website and client portal live on.',
    group: 'visitors',
    summary: ({ domain }) =>
      !domain
        ? null
        : domain.state === 'bound'
          ? { line: domain.host ?? 'Live', tone: 'set' }
          : domain.state === 'pending'
            ? { line: `${domain.host ?? 'Your domain'} — not live yet`, tone: 'attention' }
            : { line: 'No domain yet', tone: 'unset' },
  },
  {
    key: 'social',
    to: '/site/social',
    icon: Share2,
    title: 'Social profiles & map',
    note: 'Your profiles and a map link, for your footer.',
    group: 'visitors',
    summary: ({ social }) =>
      !social
        ? null
        : social.profiles === 0 && !social.map
          ? { line: 'None added yet', tone: 'unset' }
          : {
              line: [
                social.profiles ? plural(social.profiles, 'profile') : null,
                social.map ? 'map link' : null,
              ]
                .filter(Boolean)
                .join(' · '),
              tone: 'set',
            },
  },
  // w19b — C19, FR-J11 item 1: the flows a website button can book through.
  {
    key: 'booking',
    to: '/site/booking',
    icon: CalendarCheck,
    title: 'Booking flows',
    note: 'Let visitors book, verify, pay and get a confirmation — from any button.',
    group: 'visitors',
    needs: [BOOKING, 'can_view'],
    summary: ({ bookingFlows }) =>
      !bookingFlows
        ? null
        : bookingFlows.total === 0
          ? { line: 'No booking flows yet', tone: 'unset' }
          : { line: `${plural(bookingFlows.total, 'flow')} · ${bookingFlows.live} live`, tone: 'set' },
  },
  {
    key: 'policy',
    to: '/site/policy',
    icon: Scale,
    title: 'What you may say',
    note: 'Your institute’s guidelines, and your choices.',
    group: 'rules',
    summary: ({ conduct }) =>
      !conduct
        ? null
        : conduct.noRules
          ? { line: 'No conduct policy applies', tone: 'set' }
          : conduct.overrides === 0
            ? { line: 'Your institute’s defaults', tone: 'set' }
            : {
                line: `${plural(conduct.overrides, 'switch', 'switches')} changed from the defaults`,
                tone: 'set',
              },
  },
]

/** The cards by group, in list order — without a reader's `can`, every card;
 *  with it, only the cards whose `needs` the reader holds. */
/**
 * **Build my website again** — C24 item 10, ADR-0048 §3.
 *
 * Onboarding writes the firm's site when it picks a template. This is the
 * second door, for the firm that finishes its address, its services and its
 * working hours afterwards — which is most firms.
 *
 * **Confirmed, and the confirm names what it replaces.** The action rewrites
 * the draft's sections, so a firm that has written something must be told
 * before it goes. What it does *not* touch is named too, because the fear is
 * broader than the fact: not the live site, not the pictures, not the posts,
 * not the services, not the menus, not the theme.
 */
export function RebuildButton({ onBuilt }: { onBuilt: () => void }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [built, setBuilt] = useState<number | null>(null)

  const run = async () => {
    setBusy(true)
    setError(null)
    try {
      const body = await site.build()
      setBuilt(body.data.pages.length)
      setOpen(false)
      onBuilt()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Your website could not be built.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button
        variant="outline"
        className="w-full lg:w-auto"
        onClick={() => setOpen(true)}
        data-rebuild-site=""
      >
        <Wand2 size={14} aria-hidden /> Build my website again
      </Button>
      {built !== null && !open ? (
        <p role="status" className="text-xs text-primary">
          {built === 1 ? '1 page written' : `${built} pages written`} to your draft. Have a look, then
          publish when you are happy.
        </p>
      ) : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Build your website again?"
        confirmLabel="Build it"
        cancelLabel="Leave it as it is"
        busy={busy}
        busyLabel="Writing…"
        onConfirm={run}
      >
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            We will write your home, about, services and contact pages again, from what you have told
            ProDesk — your profession, your name, where you are, your services and your hours.
            <strong className="font-medium text-foreground">
              {' '}
              Anything you have written on those four pages is replaced.
            </strong>
          </p>
          <p>
            Your live site does not change until you publish. Your pictures, posts, services, menus,
            colours and any page you added yourself are left alone.
          </p>
        </div>
      </ConfirmDialog>
    </>
  )
}

export function websiteGroups(can?: (resource: string, verb: Verb) => boolean) {
  return WEBSITE_GROUPS.map((group) => ({
    group,
    cards: BUILDER_TILES.filter(
      (card) => card.group === group.key && (!card.needs || !can || can(card.needs[0], card.needs[1])),
    ),
  }))
}

/** The cards whose line needs a read of its own (the rest come from the site). */
const READ_KEYS = ['pages', 'navigation', 'blog', 'media', 'client-sign-in', 'domain', 'policy', 'booking']

/**
 * The hub's reads, each on its own: one failing leaves its card with no line
 * and the rest untouched. `pending` holds the cards still waiting.
 */
function useHubFacts() {
  const [data, setData] = useState<WebsiteHubData>({})
  const [pending, setPending] = useState<Set<string>>(() => new Set(READ_KEYS))

  useEffect(() => {
    let cancelled = false
    function read<T>(key: string, promise: Promise<T>, apply: (value: T) => WebsiteHubData) {
      promise
        .then((value) => {
          if (!cancelled) setData((prev) => ({ ...prev, ...apply(value) }))
        })
        .catch(() => undefined)
        .finally(() => {
          if (cancelled) return
          setPending((prev) => {
            const next = new Set(prev)
            next.delete(key)
            return next
          })
        })
    }
    read('pages', site.pages(), (body) => ({ pages: body.data.length }))
    read('navigation', site.navigation(), (body) => ({
      menuCustom: body.data.header_menu !== null,
    }))
    read('blog', site.posts(), (body) => ({
      posts: {
        total: body.data.length,
        published: body.data.filter((post) => post.status === 'published').length,
      },
    }))
    read('media', siteMedia.list({ limit: 1 }), (body) => ({
      pictures: typeof body.meta?.total === 'number' ? body.meta.total : body.data.length,
    }))
    read('client-sign-in', signIn.read(), (body) => ({ signIn: body.data }))
    read('domain', firm.domain(), (body) => ({ domain: body.data }))
    read('policy', firm.policy(), (body) => ({
      conduct: { overrides: overrideCount(body.data), noRules: body.data.rules === false },
    }))
    read('booking', booking.flows(), (body) => ({
      bookingFlows: {
        total: body.data.length,
        live: body.data.filter((flow) => flow.status === 'live').length,
      },
    }))
    return () => {
      cancelled = true
    }
  }, [])

  return { data, pending }
}
